import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import { components } from 'react-select'
import { useParams } from 'react-router-dom'

import RemoveIcon from 'assets/icons/remove-icon16px'
import capitalize from 'utils/capitalize'
import ActionsMenu from 'features/ActionMenu'
import IconsRow from 'components/IconsRow'
import AddCoProposerMenu from 'features/AddCoProposerMenu'
import Select from 'components/Select'
import Button from 'components/Button'
import {
  requestAddNewSiteReviewer,
  requestGetPotentialSiteReviewers,
  requestGetSiteReviewers, requestRemoveSiteReviewer
} from 'api/siteReviewer'
import { delay } from 'utils/delay'

import {
  ManageReviewerDropdownItem,
  ManageReviewerWrapper,
  ManageAvatar,
  ManageReviewerName,
  ManageReviewerLabel,
  ManageReviewerSeparator,
  ReviewerAvatar,
  ReviewerEmail,
  ReviewerName
} from './style'



const formatSiteReviewersList = (options, updateList, siteHistoryId) => {
  return options.map((reviewer) => {
    const dropdownItems = [
      {
        icon: <RemoveIcon />,
        title: 'remove',
        isDestructive: true,
        onClick: async () => {
          await requestRemoveSiteReviewer(reviewer.id, siteHistoryId)
          updateList()
        }
      }
    ]
    return {
      label: (
        <ManageReviewerDropdownItem>
          <ManageReviewerWrapper>
            <ManageAvatar>
              <ReviewerAvatar>{reviewer.User.firstName.charAt(0).toUpperCase()}</ReviewerAvatar>
            </ManageAvatar>
            <ManageReviewerName>
              <ReviewerName>
                {capitalize(reviewer.User.lastName)} {capitalize(reviewer.User.firstName)}
              </ReviewerName>
              <ReviewerEmail>{reviewer.email}</ReviewerEmail>
            </ManageReviewerName>
            </ManageReviewerWrapper>
          <div>
            <ActionsMenu table={false} manageCo={true} dropdownItems={dropdownItems} />
          </div>
        </ManageReviewerDropdownItem>
      ),
      value: 'yes',
      User: {firstName: reviewer.User.firstName}
    }
  })
}

const ManageReviewersSelectPlaceholder = (placeholderId, options) => {
  return function Placeholder(props) {
    return (
      <components.Placeholder {...props}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <ManageReviewerLabel>
            <FormattedMessage id={placeholderId} />
          </ManageReviewerLabel>
          <ManageReviewerSeparator />
          <IconsRow usersList={options} labelText='reviewers'/>
        </div>
      </components.Placeholder>
    )
  }
}

const searchStartLength = 3
const inputSaveDelayTime = 300
const searchPotentialReviewersTimeOut = delay(inputSaveDelayTime)
const ManageReviewersModal = ({toggleModal}) => {
  const params = useParams()
  const [chips, setChips] = useState([])
  const [potentialReviewers, setPotentialReviewers] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [siteReviewers, setSiteReviewers] = useState([])
  const inputRef = useRef(null)

  const isAbleToAddReviewers = () => {
    const checkInExistentUser = chips.every((chip) => {
      return chip.firstName
    })
    if (chips.length && checkInExistentUser) {
      return true
    }
  }

  const getSiteReviewers = useCallback(() => {
    requestGetSiteReviewers(params.id, params.siteHistoryId).then((data) => {
      setSiteReviewers(data)
    })
  }, [params.id, params.siteHistoryId])

  function getPotentialReviewers(){
    const excludeIds = [
      ...chips.filter((item) => {
        return typeof item.id === 'string'
      }).map((user) => {
        return user.id
      }),
      ...siteReviewers.map((proposer) => {
        return proposer.UserId
      })
    ]

    searchPotentialReviewersTimeOut(() => {
      requestGetPotentialSiteReviewers({excludeIds, searchQuery, siteId: params.id}, false).then((data) => {
        setPotentialReviewers(data)
      })
    })


  }

  useEffect(() => {
    if (searchQuery.length >= searchStartLength) {
      getPotentialReviewers()
    } else {
      setPotentialReviewers([])
    }
  }, [searchQuery])

  function onEnterPress(e) {
    const enterKey = 13
    if (e.keyCode === enterKey) {
      const found = potentialReviewers.find((user) => {
        return user.email === e.target.value
      })
      if (found) {
        setChips([...chips, found])
        setSearchQuery('')
        return
      }
      setChips([...chips, { email: e.target.value, id: Math.random(), firstName: '' }])
      setSearchQuery('')
    }
  }

  function handleChangeSearchQuery(e) {
    setSearchQuery(e.target.value)
  }

  function handleSelect(user) {
    setChips([...chips, user])
    setSearchQuery('')
    setPotentialReviewers([])
    inputRef.current?.blur()
  }

  function makeFocus(e) {
    e.preventDefault()
    inputRef.current?.focus()
  }

  async function addNewReviewers() {
    const newReviewers = chips.map((chip) => {
      return chip.id
    })

    await requestAddNewSiteReviewer({ siteId: params.id, newReviewers }, params.siteHistoryId)
    setChips([])
    await getSiteReviewers()
  }

  useEffect(() => {
    getSiteReviewers()
  }, [getSiteReviewers])

  const formattedReviewerList = useMemo(() => {
    return formatSiteReviewersList(siteReviewers, getSiteReviewers, params.siteHistoryId)
  }, [siteReviewers, getSiteReviewers, params.siteHistoryId])

  return (
    <div>
      <AddCoProposerMenu
        makeFocus={makeFocus}
        inputRef={inputRef}
        searchQuery={searchQuery}
        handleChangeQuery={handleChangeSearchQuery}
        chips={chips}
        handleSelect={handleSelect}
        potentialCoProposers={potentialReviewers}
        setChips={setChips}
        setQuery={setSearchQuery}
        onEnterPress={onEnterPress}
        reviewersMenu={true}
      />
      {siteReviewers.length > 0 && (
        <Select
          placeholderComponent={ManageReviewersSelectPlaceholder('manageUsers', formatSiteReviewersList(siteReviewers, getSiteReviewers, params.siteHistoryId))}
          field="manageUsers"
          value={null}
          options={formattedReviewerList}
          height="48px"
          placeholder="manageUsers"
          menuPlacement="bottom"
          closeMenuOnSelect={false}
          marginBottom="16px"
        />
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <div style={{ marginRight: '16px' }}>
          <Button dataTest="cancel" type="secondary" onClick={toggleModal} marginTop="16px">
            <FormattedMessage id="cancel" />
          </Button>
        </div>
        <Button
          dataTest="submit"
          type={isAbleToAddReviewers()? 'primary' : 'disabledPrimary'}
          onClick={addNewReviewers}
          isDisabled={!isAbleToAddReviewers()}
          marginTop="16px"
        >
          <FormattedMessage id="sendInvites" />
        </Button>
      </div>
    </div>
  )
}

export default ManageReviewersModal

ManageReviewersModal.propTypes = {
  toggleModal: PropTypes.func.isRequired
}
